import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { toKr } from '@heivol-finance/calc';
import { currencySymbol, groupOre, parseKrToOre } from '../lib/format.js';

/** Page title + optional subtitle. */
export function PageHeader({ title, subtitle }: { title: string; subtitle?: ReactNode }): JSX.Element {
  return (
    <div className="mb-6">
      <h1 className="text-hv-xl font-medium text-hv-text">{title}</h1>
      {subtitle ? <p className="mt-1 text-hv-sm text-hv-text-secondary">{subtitle}</p> : null}
    </div>
  );
}

/** A surface panel. */
export function Card({ children, className = '' }: { children: ReactNode; className?: string }): JSX.Element {
  return (
    <div className={`rounded-hv-lg border border-hv-border bg-hv-surface shadow-hv-sm ${className}`}>{children}</div>
  );
}

/**
 * A collapsible surface panel. The header is a flex row: a chevron + the always-visible
 * `summary` form a single toggle `<button>` (keyboard-accessible, aria-expanded/controls),
 * and any `actions` (e.g. a delete button) render as a *sibling* — never nested inside the
 * toggle — so clicking them doesn't collapse the card. The `children` body renders below
 * only when open. Collapsed by default; a click expands.
 */
export function CollapsibleCard({
  summary,
  children,
  actions,
  defaultOpen = false,
  className = '',
}: {
  summary: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  defaultOpen?: boolean;
  className?: string;
}): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();

  return (
    <Card className={`overflow-hidden ${className}`}>
      <div className="flex items-center gap-2 border-b border-hv-border px-4 py-3">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls={bodyId}
          className="flex flex-1 items-center gap-2 text-left text-hv-sm font-medium text-hv-text focus-visible:shadow-hv-focus"
        >
          <span
            aria-hidden="true"
            className={`inline-block text-hv-text-tertiary transition-transform ${open ? 'rotate-90' : ''}`}
          >
            ▸
          </span>
          <span className="min-w-0 flex-1">{summary}</span>
        </button>
        {actions ? <div className="shrink-0">{actions}</div> : null}
      </div>
      {open ? <div id={bodyId}>{children}</div> : null}
    </Card>
  );
}

export type Tone = 'default' | 'ai' | 'success' | 'warning' | 'chat';

const toneClass: Record<Tone, string> = {
  default: 'border-hv-border bg-hv-surface text-hv-text',
  ai: 'border-hv-ai-border bg-hv-ai-surface text-hv-ai-text',
  success: 'border-hv-success bg-hv-success-surface text-hv-success-text',
  warning: 'border-hv-warning bg-hv-warning-surface text-hv-warning-text',
  chat: 'border-hv-chat-border bg-hv-chat-surface text-hv-chat-text',
};

/** A big stat: label, value, optional hint, tinted by tone. */
export function StatCard({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
}): JSX.Element {
  return (
    <div className={`rounded-hv-lg border p-4 ${toneClass[tone]}`}>
      <div className="text-hv-xs uppercase tracking-wide opacity-80">{label}</div>
      <div className="mt-1 text-hv-xl font-medium tabular-nums">{value}</div>
      {hint ? <div className="mt-1 text-hv-xs opacity-80">{hint}</div> : null}
    </div>
  );
}

export function Button({
  children,
  onClick,
  variant = 'primary',
  type = 'button',
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'ghost';
  type?: 'button' | 'submit';
  disabled?: boolean;
}): JSX.Element {
  const cls =
    variant === 'primary'
      ? 'bg-hv-ai text-hv-text-inverse hover:bg-hv-ai-hover'
      : variant === 'secondary'
        ? 'border border-hv-border bg-hv-surface text-hv-text hover:bg-hv-surface-alt'
        : 'text-hv-text-secondary hover:bg-hv-surface-alt';
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-hv-md px-3 py-1.5 text-hv-sm font-medium transition-colors focus-visible:shadow-hv-focus disabled:opacity-50 ${cls}`}
    >
      {children}
    </button>
  );
}

/** Small round destructive icon button (row delete). */
export function DeleteButton({ onClick, label = 'Delete' }: { onClick: () => void; label?: string }): JSX.Element {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      className="rounded-hv-md px-2 py-1 text-hv-text-tertiary transition-colors hover:bg-hv-error-surface hover:text-hv-error-text focus-visible:shadow-hv-focus"
    >
      ✕
    </button>
  );
}

const inputCls =
  'w-full rounded-hv-md border border-hv-border bg-hv-surface px-2.5 py-1.5 text-hv-sm text-hv-text outline-none focus-visible:shadow-hv-focus';

/** Text input that commits on blur (and Enter) — good for inline table editing. */
export function TextField({
  value,
  onCommit,
  placeholder,
  className = '',
}: {
  value: string;
  onCommit: (v: string) => void;
  placeholder?: string;
  className?: string;
}): JSX.Element {
  return (
    <input
      type="text"
      defaultValue={value}
      placeholder={placeholder}
      onBlur={(e) => e.target.value !== value && onCommit(e.target.value)}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      className={`${inputCls} ${className}`}
    />
  );
}

/** The raw editable form of an øre value (plain digits, e.g. "42000" or "42000.5" — no grouping). */
function rawKr(ore: number): string {
  return String(toKr(ore));
}

/**
 * Money input: stores ØRE, displays the amount grouped in the current locale (e.g. "42.000")
 * when blurred, and the raw editable digits (e.g. "42000") while focused for easy typing.
 * Commits on blur/Enter via onCommit(ore). The current currency symbol is shown as a suffix
 * adornment (same visual pattern NumberField uses for its `suffix`). Drop-in replacement —
 * `value` is øre, same props as before.
 */
export function OreField({
  value,
  onCommit,
  className = '',
}: {
  value: number;
  onCommit: (ore: number) => void;
  className?: string;
}): JSX.Element {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');
  const ref = useRef<HTMLInputElement>(null);

  // Re-sync the grouped display when the external value changes and we're not editing.
  const display = focused ? draft : groupOre(value);

  useEffect(() => {
    if (!focused) setDraft(rawKr(value));
  }, [value, focused]);

  const commit = (text: string): void => {
    const ore = parseKrToOre(text);
    if (Number.isFinite(ore) && ore !== value) onCommit(ore);
  };

  return (
    <div className="flex items-center gap-1">
      <input
        ref={ref}
        type="text"
        inputMode="decimal"
        value={display}
        onFocus={() => {
          setDraft(rawKr(value));
          setFocused(true);
        }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => {
          setFocused(false);
          commit(e.target.value);
        }}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        className={`${inputCls} text-right tabular-nums ${className}`}
      />
      <span className="text-hv-xs text-hv-text-tertiary">{currencySymbol()}</span>
    </div>
  );
}

/**
 * Money field over WHOLE-KRONER state (plain number), for screens whose state is kroner
 * (LoanCalculator). Shows thousands grouping in the current locale when blurred and the raw
 * digits when focused, with the dynamic currency symbol as a suffix. `suffix` overrides the
 * symbol (e.g. "kr./mo.").
 *
 * NOT merged with OreField on purpose — the semantics differ: OreField stores øre and only
 * commits when the value changed; MoneyField stores kroner (possibly fractional) and commits
 * on every blur, driving live recalculation via onChange-style state setters.
 */
export function MoneyField({
  label,
  value,
  onChange,
  suffix,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  suffix?: string;
}): JSX.Element {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');
  const display = focused ? draft : groupOre(Math.round(value * 100));

  useEffect(() => {
    if (!focused) setDraft(String(value));
  }, [value, focused]);

  return (
    <label className="block">
      <span className="text-hv-sm text-hv-text-secondary">{label}</span>
      <div className="mt-1 flex items-center rounded-hv-md border border-hv-border bg-hv-surface focus-within:shadow-hv-focus">
        <input
          type="text"
          inputMode="decimal"
          value={display}
          onFocus={() => {
            setDraft(String(value));
            setFocused(true);
          }}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={(e) => {
            setFocused(false);
            onChange(parseKrToOre(e.target.value) / 100);
          }}
          className="w-full bg-transparent px-3 py-2 text-right tabular-nums text-hv-text outline-none"
        />
        <span className="px-3 text-hv-sm text-hv-text-tertiary">{suffix ?? currencySymbol()}</span>
      </div>
    </label>
  );
}

/** Plain number input (rates, ages, percents) that commits on blur. */
export function NumberField({
  value,
  onCommit,
  step,
  suffix,
  className = '',
}: {
  value: number;
  onCommit: (n: number) => void;
  step?: number;
  suffix?: string;
  className?: string;
}): JSX.Element {
  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        step={step}
        defaultValue={value}
        onBlur={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n) && n !== value) onCommit(n);
        }}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        className={`${inputCls} text-right tabular-nums ${className}`}
      />
      {suffix ? <span className="text-hv-xs text-hv-text-tertiary">{suffix}</span> : null}
    </div>
  );
}

export function Select<T extends string>({
  value,
  options,
  onChange,
  className = '',
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  className?: string;
}): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      className={`${inputCls} ${className}`}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/** Dashed empty-state box with an optional call to action. */
export function EmptyState({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="rounded-hv-lg border border-dashed border-hv-border bg-hv-surface p-8 text-center text-hv-sm text-hv-text-tertiary">
      {children}
    </div>
  );
}
